// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface AssignedAthlete {
  id: string;
  username: string;
  email: string;
  sport_id: string;
  grad_year?: number;
  avatar_url?: string;
  assigned_at: string;
}

interface ScoutTask {
  id: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'low' | 'normal' | 'high';
  due_date?: string;
  athlete_id?: string;
  athlete_username?: string;
  created_at: string;
}

interface PendingRequest {
  id: string;
  athlete_email: string;
  message?: string;
  requested_at: string;
  status: 'pending' | 'approved' | 'rejected';
}

export const useScoutAssignments = () => {
  const { profile } = useAuth();
  const [assignedAthletes, setAssignedAthletes] = useState<AssignedAthlete[]>([]);
  const [scoutTasks, setScoutTasks] = useState<ScoutTask[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🔍 useScoutAssignments: Starting scout assignments fetch');
    console.log('🔍 useScoutAssignments: Current profile:', profile);

    const fetchScoutData = async () => {
      if (!profile) {
        console.log('❌ useScoutAssignments: No profile found, aborting fetch');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log('📊 useScoutAssignments: Scout data fetching for profile:', profile.id);
        
        // Fetch active scout-athlete relationships with sport information
        console.log('📡 useScoutAssignments: Fetching scout-athlete relationships');
        const { data: relationships, error: relationshipsError } = await supabase
          .from('scout_athlete_relationships')
          .select(`
            *,
            athlete:athlete_id (
              id,
              username,
              email,
              grad_year,
              avatar_url,
              sport_id,
              sports(name)
            )
          `)
          .eq('scout_id', profile.id)
          .eq('status', 'active');
          
        console.log('🔍 useScoutAssignments: Active scout relationships response:', { relationships, relationshipsError });

        if (relationshipsError) {
          console.error('❌ useScoutAssignments: Error fetching scout relationships:', relationshipsError);
          setError(relationshipsError.message);
        } else if (relationships && relationships.length > 0) {
          console.log('📊 useScoutAssignments: Processing relationships, count:', relationships.length);
          
          // Format the relationships data
          const formattedAthletes = relationships.map((relationship, index) => {
            console.log(`📊 useScoutAssignments: Processing relationship ${index}:`, relationship);
            
            const athlete = Array.isArray(relationship.athlete) ? relationship.athlete[0] : relationship.athlete;
            console.log(`📊 useScoutAssignments: Athlete data for relationship ${index}:`, athlete);
            
            const formattedAthlete = {
              id: relationship.athlete_id,
              username: athlete?.username || 'Unknown Athlete',
              email: athlete?.email || '',
              sport_id: athlete?.sport_id || '',
              grad_year: athlete?.grad_year,
              avatar_url: athlete?.avatar_url,
              assigned_at: relationship.created_at
            };
            
            console.log(`📊 useScoutAssignments: Formatted athlete ${index}:`, formattedAthlete);
            return formattedAthlete;
          });
          
          console.log('✅ useScoutAssignments: All formatted athletes:', formattedAthletes);
          setAssignedAthletes(formattedAthletes);
        } else {
          console.log('📭 useScoutAssignments: No active scout relationships found');
          setAssignedAthletes([]);
        }

        // Fetch assigned tasks from scout_task_assignments table
        console.log('📡 useScoutAssignments: Fetching assigned tasks');
        const { data: assignedTasks, error: assignedTasksError } = await supabase
          .from('scout_task_assignments')
          .select(`
            *,
            task:scout_tasks!task_id (
              id,
              title,
              description,
              priority,
              due_date,
              created_at
            ),
            athlete:profiles!athlete_profile_id (
              id,
              username,
              name,
              email
            )
          `)
          .eq('assigned_by', profile.id)
          .order('assigned_at', { ascending: false });

        console.log('📊 useScoutAssignments: Assigned tasks response:', { assignedTasks, assignedTasksError });

        if (assignedTasksError) {
          console.error('❌ useScoutAssignments: Error fetching assigned tasks:', assignedTasksError);
        } else {
          console.log('📊 useScoutAssignments: Processing assigned tasks, count:', assignedTasks?.length || 0);
          
          // Format the assigned tasks data for dashboard display
          const formattedAssignedTasks = (assignedTasks || []).map((assignment, index) => {
            console.log(`📊 useScoutAssignments: Processing assigned task ${index}:`, assignment);
            
            const task = Array.isArray(assignment.task) ? assignment.task[0] : assignment.task;
            const athlete = Array.isArray(assignment.athlete) ? assignment.athlete[0] : assignment.athlete;
            
            const formattedTask = {
              id: assignment.id,
              title: task?.title || 'Unknown Task',
              description: task?.description,
              status: assignment.status,
              priority: task?.priority || 'normal',
              due_date: assignment.due_date,
              athlete_id: assignment.athlete_profile_id,
              athlete_username: athlete?.username || athlete?.name || 'Unknown Athlete',
              created_at: assignment.assigned_at
            };
            
            console.log(`📊 useScoutAssignments: Formatted assigned task ${index}:`, formattedTask);
            return formattedTask;
          });
          
          console.log('✅ Assigned tasks fetched:', formattedAssignedTasks);
          setScoutTasks(formattedAssignedTasks);
        }

        // Fetch pending requests
        console.log('📡 useScoutAssignments: Fetching pending requests');
        const { data: pendingReqs, error: pendingError } = await supabase
          .from('scout_athlete_requests')
          .select('*')
          .eq('scout_id', profile.id)
          .eq('status', 'pending')
          .order('requested_at', { ascending: false });

        console.log('📊 useScoutAssignments: Pending requests response:', { pendingReqs, pendingError });

        if (pendingError) {
          console.error('❌ useScoutAssignments: Error fetching pending scout requests:', pendingError);
        } else {
          console.log('✅ useScoutAssignments: Setting pending requests:', pendingReqs || []);
          setPendingRequests(pendingReqs || []);
        }

      } catch (err) {
        console.error('❌ useScoutAssignments: Error fetching scout data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load scout data');
      } finally {
        console.log('🏁 useScoutAssignments: Fetch process completed');
        setIsLoading(false);
      }
    };

    fetchScoutData();
  }, [profile]);

  const requestAthleteAccess = async (athleteProfileId: string, message?: string) => {
    console.log('📝 useScoutAssignments: Requesting athlete access', { athleteProfileId, message });
    
    if (!profile) {
      console.log('❌ useScoutAssignments: No profile found for access request');
      throw new Error('No profile found');
    }

    // Validate required fields
    const scout_id = profile.id;
    const athlete_id = athleteProfileId;
    
    if (!scout_id) {
      console.log('❌ useScoutAssignments: Scout profile ID is missing');
      throw new Error('Scout profile ID is required');
    }
    
    if (!athlete_id) {
      console.log('❌ useScoutAssignments: Athlete profile ID is missing');
      throw new Error('Athlete profile ID is required');
    }

    const requestData = {
      scout_id,
      athlete_id,
      message: message || '',
      status: 'pending'
    };

    console.log('📝 useScoutAssignments: Submitting scout athlete request:', requestData);

    // Insert into scout_athlete_requests with correct field names
    const { data, error } = await supabase
      .from('scout_athlete_requests')
      .insert([requestData]);

    if (error) {
      console.error('❌ useScoutAssignments: Error inserting scout request:', error);
      throw error;
    }

    console.log('✅ useScoutAssignments: Scout request submitted successfully:', data);

    // Refresh pending requests
    console.log('🔄 useScoutAssignments: Refreshing pending requests');
    const { data: requests } = await supabase
      .from('scout_athlete_requests')
      .select('*')
      .eq('scout_id', profile.id)
      .eq('status', 'pending')
      .order('requested_at', { ascending: false });

    console.log('✅ useScoutAssignments: Updated pending requests:', requests || []);
    setPendingRequests(requests || []);
  };

  console.log('🔄 useScoutAssignments: Hook returning data:', {
    assignedAthletes: assignedAthletes.length,
    scoutTasks: scoutTasks.length,
    pendingRequests: pendingRequests.length,
    isLoading,
    error
  });

  return {
    assignedAthletes,
    scoutTasks,
    pendingRequests,
    isLoading,
    error,
    requestAthleteAccess
  };
};
